import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';

import { useAppSelector } from '@/store';
import { selectLeaseIds, selectUser } from '@/store/selectors/userSelectors';
import { HomeAPI } from '@/services/residents/api/HomeAPI';
import { WifiInfo, WifiSupportType } from '@/types/residents/wifi';
import { Lease } from '@/types/residents/lease';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { EmptyStateView } from '@/components/common/EmptyStateView';
import { DEFAULT_WIFI_SUPPORT_PHONE } from '@/constants/residents/shared/wifi';
import { useLazyGetLeaseQuery } from '@/store/api';
import { isWifiEligible } from '@/utils/residents/wifi/eligibility';
import { extractWifi } from '@/utils/residents/wifi/parse';
import { buildSupportOptions } from '@/utils/residents/wifi/support';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import WifiService from '@/services/residents/api/wifi/WifiService';
import { showAlert } from '@/utils/common/alert';

import { logger } from '@/utils/common/logger';

import { PageHeader } from '@/components/residents/redesign';
import {
  WifiInfoGlassCard,
  WifiSupportGlassCard,
} from '@/components/residents/redesign/wifi';
import { DesktopWifiScreenSkeleton } from './DesktopWifiScreenSkeleton';

type LeaseWifi = {
  leaseId: number;
  wifi: WifiInfo;
  leaseUnit?: string;
};

export const DesktopWifiScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const leaseIds = useAppSelector(selectLeaseIds);
  const user = useAppSelector(selectUser);
  const [items, setItems] = useState<LeaseWifi[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLease] = useLazyGetLeaseQuery();

  const loadWifiForLeases = useCallback(async () => {
    let cancelled = false;
    if (!leaseIds || leaseIds.length === 0) {
      if (!cancelled) setItems([]);
      return;
    }
    setLoading(true);
    try {
      const results = await Promise.all(
        leaseIds.map(async (id) => {
          try {
            let lease: (Partial<Lease> & { subStatus?: string; unitNumber?: string }) | undefined;
            try {
              const resp: any = await fetchLease({ leaseId: id, includeTransactions: false, includePayments: false }).unwrap();
              lease = resp?.response?.lease;
            } catch (e) {
              logger.warn('wifiLeaseFetchFailed', e);
            }

            if (lease && !isWifiEligible(lease)) return null;
            const unitInfo = await HomeAPI.getUnitInfo(id);
            const wifiInfo = extractWifi(unitInfo);
            if (!wifiInfo) return null;
            return { leaseId: id, wifi: wifiInfo, leaseUnit: lease?.unitNumber } as LeaseWifi;
          } catch (e) {
            logger.warn('wifiUnitInfoFetchFailed', e);
          }
          return null;
        })
      );
      if (!cancelled) setItems(results.filter(Boolean) as LeaseWifi[]);
    } finally {
      if (!cancelled) setLoading(false);
    }
  }, [leaseIds, fetchLease]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await loadWifiForLeases();
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loadWifiForLeases]);

  const firstWifi = useMemo(() => items[0]?.wifi, [items]);
  const propertyName = user?.property?.marketingName;
  const isEmpty = !loading && items.length === 0;

  const supportOptions = useMemo(
    () => buildSupportOptions(firstWifi || undefined),
    [firstWifi],
  );


  const handleSupportPress = useCallback(async (type: WifiSupportType, value: string) => {
    try {
      switch (type) {
        case WifiSupportType.TEXT:
          await WifiService.openSupportText(value);
          break;
        case WifiSupportType.EMAIL:
          await WifiService.openSupportEmail(value);
          break;
        case WifiSupportType.WEBSITE:
          await WifiService.openSupportWebsite(value);
          break;
      }
    } catch {
      showAlert('Error', `Could not open ${type} app`);
    }
  }, []);

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.rootContent}
      showsVerticalScrollIndicator={false}
    >
      <PageHeader
        title="WiFi"
        subtitle={[propertyName, 'Connection info & support'].filter(Boolean).join(' · ')}
      />
      {loading ? (
        <DesktopWifiScreenSkeleton />
      ) : isEmpty ? (
        <EmptyStateView
          icon={<RESIDENTS_SVG_COMPONENTS.CONNECTION width={67} height={68} color={colors.text.muted} />}
          title="No Wifi Information Available"
          message={`Your leases have no available wifi information. Contact support at ${DEFAULT_WIFI_SUPPORT_PHONE}`}
        />
      ) : (
        <>
          <View style={styles.rowWrap}>
            <View style={styles.colLeft}>
              {items.map((item) => (
                <WifiInfoGlassCard
                  key={item.leaseId}
                  wifiInfo={item.wifi}
                  propertyName={propertyName}
                  leaseUnit={item.leaseUnit ? `Unit ${item.leaseUnit}` : undefined}
                />
              ))}
            </View>
            <View style={styles.colRight}>
              {supportOptions.length > 0 && (
                <WifiSupportGlassCard options={supportOptions} onPress={handleSupportPress} />
              )}
            </View>
          </View>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  rootContent: {
    padding: 24,
  },
  rowWrap: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
  },
  colLeft: {
    flex: 1,
  },
  colRight: {
    flex: 1,
    maxWidth: 380,
  },
});

export default DesktopWifiScreen;
